import { useState, useEffect } from "react";
import { toast } from "sonner";

export function usePlatformAdminPanel({ adminAxios, onLogout }) {
  // Ler tab inicial da URL (?tab=jarvis ou /platform-admin/jarvis)
  const initialTab = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("tab")) return params.get("tab");
      const pathParts = window.location.pathname.split("/").filter(Boolean);
      const idx = pathParts.indexOf("platform-admin");
      if (idx !== -1 && pathParts[idx + 1]) return pathParts[idx + 1];
    } catch {}
    return "overview";
  })();
  const [tab, setTab] = useState(initialTab);
  const [overview, setOverview] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [leads, setLeads] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [restaurantDetail, setRestaurantDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const [auditResult, setAuditResult] = useState(null);
  const [auditing, setAuditing] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [blocking, setBlocking] = useState(false);

  const loadData = () => {
    setTab((prev) => {
      // trigger re-fetch by toggling via a temporary state change
      return prev;
    });
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        if (tab === "overview" || !overview) {
          const res = await adminAxios.get("/platform-admin/overview");
          setOverview(res.data);
        }
        if (tab === "restaurants") {
          const res = await adminAxios.get("/platform-admin/restaurants");
          setRestaurants(Array.isArray(res.data) ? res.data : res.data?.restaurants || []);
        }
        if (tab === "affiliates") {
          const res = await adminAxios.get("/platform-admin/affiliates");
          setAffiliates(Array.isArray(res.data) ? res.data : res.data?.affiliates || []);
        }
        if (tab === "leads") {
          const res = await adminAxios.get("/platform-admin/leads");
          setLeads(Array.isArray(res.data) ? res.data : res.data?.leads || []);
        }
        if (tab === "settings") {
          const res = await adminAxios.get("/platform-admin/settings");
          setSettings(res.data);
        }
      } catch (err) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          toast.error("Sessao expirada ou acesso negado");
          onLogout();
          return;
        }
        toast.error("Erro ao carregar dados");
      }
      setLoading(false);
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, adminAxios]);

  const refreshTab = (targetTab) => {
    setTab("overview");
    setTimeout(() => setTab(targetTab), 100);
  };

  const loginAsRestaurant = async (restaurantId, restaurantName) => {
    setImpersonating(true);
    try {
      const res = await adminAxios.post(
        `/platform-admin/login-as-restaurant/${restaurantId}`
      );
      const { access_token } = res.data;
      const currentToken = localStorage.getItem("token");
      const currentRefresh = localStorage.getItem("refresh_token");
      localStorage.setItem("admin_original_token", currentToken);
      localStorage.setItem("admin_original_refresh", currentRefresh);
      localStorage.setItem("token", access_token);
      localStorage.removeItem("refresh_token");
      toast.success(`Entrando como: ${restaurantName}`);
      window.location.href = "/dashboard";
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Erro ao entrar no restaurante"
      );
    }
    setImpersonating(false);
  };

  const runAudit = async (restaurantId) => {
    setAuditing(true);
    setAuditResult(null);
    try {
      const res = await adminAxios.post(
        `/platform-admin/audit/${restaurantId}`
      );
      setAuditResult(res.data);
    } catch {
      toast.error("Erro ao executar auditoria");
    }
    setAuditing(false);
  };

  const runFix = async (restaurantId) => {
    setFixing(true);
    try {
      const res = await adminAxios.post(
        `/platform-admin/audit/${restaurantId}/fix`
      );
      toast.success(`${res.data.fixes_applied} correcao(oes) aplicada(s)!`);
      const audit = await adminAxios.post(
        `/platform-admin/audit/${restaurantId}`
      );
      setAuditResult(audit.data);
      const detail = await adminAxios.get(
        `/platform-admin/restaurants/${restaurantId}`
      );
      setRestaurantDetail(detail.data);
    } catch {
      toast.error("Erro ao aplicar correcoes");
    }
    setFixing(false);
  };

  const toggleBlock = async (restaurantId, currentStatus) => {
    const action = currentStatus === "suspended" ? "unblock" : "block";
    const confirmMsg =
      action === "block"
        ? "Tem certeza que quer BLOQUEAR este restaurante? Menu publico e QR codes serao desativados."
        : "Desbloquear este restaurante? O acesso sera restaurado imediatamente.";
    if (!window.confirm(confirmMsg)) return;
    setBlocking(true);
    try {
      const res = await adminAxios.post(
        `/platform-admin/restaurants/${restaurantId}/${action}`
      );
      toast.success(res.data.message);
      const detail = await adminAxios.get(
        `/platform-admin/restaurants/${restaurantId}`
      );
      setRestaurantDetail(detail.data);
      refreshTab("restaurants");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao alterar status");
    }
    setBlocking(false);
  };

  const openRestaurantDetail = async (restaurantId) => {
    setDetailLoading(true);
    setSelectedRestaurant(restaurantId);
    setAuditResult(null);
    try {
      const res = await adminAxios.get(
        `/platform-admin/restaurants/${restaurantId}`
      );
      setRestaurantDetail(res.data);
    } catch {
      toast.error("Erro ao carregar detalhes");
    }
    setDetailLoading(false);
  };

  const closeDetail = () => {
    setSelectedRestaurant(null);
    setRestaurantDetail(null);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await adminAxios.put("/platform-admin/settings", settings);
      toast.success("Configuracoes salvas!");
    } catch {
      toast.error("Erro ao salvar");
    }
    setSaving(false);
  };

  const filteredRestaurants = (Array.isArray(restaurants) ? restaurants : []).filter(
    (r) =>
      !search ||
      r.name?.toLowerCase().includes(search.toLowerCase()) ||
      r.email?.toLowerCase().includes(search.toLowerCase())
  );

  return {
    tab, setTab,
    overview,
    restaurants, filteredRestaurants,
    affiliates,
    leads,
    settings, setSettings,
    loading,
    search, setSearch,
    saving,
    selectedRestaurant,
    restaurantDetail,
    detailLoading,
    impersonating,
    auditResult,
    auditing,
    fixing,
    blocking,
    loginAsRestaurant,
    runAudit,
    runFix,
    toggleBlock,
    openRestaurantDetail,
    closeDetail,
    saveSettings,
    refreshTab,
  };
}
